import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAuthSession } from 'aws-amplify/auth';
import styled from '@emotion/styled';

import { RootState } from 'src/shared/store/rootReducer';
import { formatPrice } from '../../common/utils';
import { CartItem } from '../../cart/components/CartItem';
import { breakpoints, Checkbox, Divider, Spinner } from '../../../design-system';
import { UnknownAction } from 'redux';
import { PAYPAL_EXPRESS_CANCEL, PAYPAL_EXPRESS_OPT_IN } from '../locale';
import { colors, opacities } from '../../../design-system/systemprovider';
import { updateNewsletter } from '../../account/actions';
import { ERR_AUTH } from '../../auth/locale';
import { useNavigate } from 'react-router-dom';
import paths from '../../routing/paths';
import { durations } from '../../common/constants';
import { loadCart, setStep, updatePaypalECS } from '../../cart/actions';
import { CartStep } from '../../cart/types';
import { getRegionName } from '../../product/utils';
import { getBFFData, Mutations } from '../../api';
import { Logo } from '../../common/components/Logo';

const StyledHeader = styled.header`
  margin: 32px 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledReviewPage = styled.div<{ isError: boolean }>`
  margin-bottom: 24px;

  @media (min-width: ${breakpoints.S}px) {
    margin: auto 16px 24px 16px;
  }

  @media (min-width: ${breakpoints.M}px) {
    width: 80%;
    margin: auto auto 24px auto;
  }

  .review-page-title {
    display: none;
    font-size: 1.6rem;
    line-height: 2.4rem;
    font-weight: 700;
    padding: 0 16px;

    @media (min-width: ${breakpoints.S}px) {
      padding: 0;
      display: block;
    }
  }

  .review-page-title-mobile {
    display: block;
    font-size: 1.6rem;
    line-height: 2.4rem;
    font-weight: 700;
    padding: 0 16px;

    @media (min-width: ${breakpoints.S}px) {
      display: none;
    }
  }

  .review-page-content {
    display: flex;
    flex-direction: column-reverse;
    gap: 24px;

    @media (min-width: ${breakpoints.S}px) {
      flex-direction: row;
      gap: 64px;
    }
  }

  .review-page-product-container {
    width: 100%;

    @media (min-width: ${breakpoints.S}px) {
      width: calc(60% - 32px);
    }
  }

  .review-page-informations-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-left: auto;
    margin-right: auto;
    width: calc(100% - 32px);

    @media (min-width: ${breakpoints.S}px) {
      width: calc(40% - 32px);

      height: fit-content;
      position: sticky;
      top: 16px;
    }

    h3 {
      margin: 8px 0;
      font-size: 1.6rem;
      font-weight: 700;
      color: ${colors.BLACK};
    }

    p {
      margin: 8px 0;
      font-size: 1.4rem;
      font-weight: 500;
      color: ${colors.MAIN_GREY};
    }
  }

  .review-page-total-container,
  .review-page-phone-container {
    display: flex;
    gap: 8px;
    justify-content: space-between;
    p {
      font-weight: 700;
      font-size: 1.6rem;
    }
  }

  .paypal-express-review-page-news-letter-description {
    margin: 0;
    font-size: 1.2rem;
    color: ${colors.GREY};
  }

  .papypal-express-pay-btn,
  .paypal-express-reviw-page-cancel-btn {
    color: ${colors.BLACK};
    font-weight: 700;
    font-size: 1.6rem;
    height: 44px;
    border: none;
    border-radius: 8px;
    padding: 0;
    transition: opacity ${durations.ENTER}ms ease-in-out;

    @media (hover: hover) {
      :hover {
        cursor: pointer;
        opacity: ${opacities.HOVERED};
      }
    }
  }

  .papypal-express-pay-btn {
    background-color: ${colors.PAYPAL_YELLOW};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    pointer-events: ${({ isError }) => (isError ? 'none' : 'initial')};

    .paypal-express-btn-text {
      margin-bottom: -4px;
    }
  }

  .paypal-express-reviw-page-cancel-btn {
    color: ${colors.WHITE};
    background-color: ${colors.BLACK};
  }

  p.light {
    margin: 8px 0 0 0;
    font-weight: 500;
    font-size: 1.4rem;
    color: ${colors.MAIN_GREY};
  }

  p.discount-color {
    color: ${colors.MAIN_SUCCESS};
  }
`;

export const PaypalECSReviewPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cart: originalCart, isFetching } = useSelector((state: RootState) => state.cart);
  const { cart, data, details, cognito } = useSelector((state: RootState) => state.cart.paypalECS);
  const { items, shippingAmount, originalBaseTotal, total, totalCouponAmount, totalDiscount } =
    cart;
  const { billingAddress, deliveryAddress, authorizedEvent } = data;

  const {
    street: streetB,
    postalCode: postalCodeB,
    city: cityB,
    country: countryB,
  } = billingAddress;
  const {
    street: streetD,
    postalCode: postalCodeD,
    city: cityD,
    country: countryD,
  } = deliveryAddress;
  const { payer } = authorizedEvent;
  const { email_address: email } = payer;
  const { national_number: phone } = payer.phone.phone_number;

  const billingStreet = `${streetB ?? streetD ?? ''}`;
  const billingPostalcode = postalCodeB ?? postalCodeD ?? '';
  const billingCity = cityB ?? cityD;
  const billingCountry = countryB ?? countryD;
  const deliveryStreet = streetD ?? '';
  const deliveryPostalCode = postalCodeD ?? '';
  const deliveryCity = cityD ?? '';

  const { isLoggedIn } = useSelector((state: RootState) => state.auth);
  const { user } = useSelector((state: RootState) => state.account);
  const giftItemTextColor =
    useSelector((state: RootState) => state.cms.cart?.gift_text_color) ?? 'BLACK';

  const [optinIsChecked, setOptinIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const payEvent = async () => {
    const credentials = await fetchAuthSession();
    const attemptCognitoId = credentials.identityId;
    if (
      attemptCognitoId !== cognito.initialCognitoId ||
      (
        parseFloat(originalCart.total?.toString() ?? '0') -
        parseFloat(originalCart.shippingAmount?.toString() ?? '0')
      ).toFixed(2) !==
        (
          parseFloat(cart.total?.toString() ?? '0') - parseFloat(shippingAmount?.toString() ?? '0')
        ).toFixed(2) ||
      isFetching
    ) {
      setIsError(true);
      return;
    }
    setIsLoading(true);
    let isOk = true;
    if (optinIsChecked) {
      if (isLoggedIn && !user?.optInEmail) {
        dispatch(updateNewsletter({ optInEmail: true }) as unknown as UnknownAction);
      }
      if (!isLoggedIn) {
        const response = await getBFFData(Mutations.subscribeNewsletter, {
          payload: { MAIL: email, ORIGIN: 'PaypalECS' },
        });
        isOk = response.data.subscribeNewsletters?.success;
      }
    }
    if (!isOk) {
      setIsError(true);
      setIsLoading(false);
    } else if (isOk && !isError && !isLoading) {
      const { details: detailsFolder, paymentData } = details;
      const response = await getBFFData(Mutations.paypalDetails, {
        shipping: {
          postalCode: deliveryAddress?.postalCode ?? '',
          countryCode: deliveryAddress?.country ?? '',
          city: deliveryAddress?.city ?? '',
          street: deliveryAddress?.street ?? '',
          stateOrProvince: deliveryAddress?.stateOrProvince ?? '',
          houseNumberOrName: deliveryAddress?.houseNumberOrName ?? '',
          firstName: deliveryAddress?.firstName ?? '',
          lastName: deliveryAddress?.lastName ?? '',
        },
        billing: {
          postalCode: billingAddress?.postalCode ?? '',
          countryCode: billingAddress?.country ?? '',
          city: billingAddress?.city ?? '',
          street: billingAddress?.street ?? '',
          stateOrProvince: billingAddress?.stateOrProvince ?? '',
          houseNumberOrName: billingAddress?.houseNumberOrName ?? '',
          firstName: billingAddress?.firstName ?? '',
          lastName: billingAddress?.lastName ?? '',
        },
        payer: {
          email,
          firstName: payer.name.given_name,
          lastName: payer.name.surname,
          phone,
        },
        details: JSON.stringify(detailsFolder),
        paymentData: paymentData,
      });
      const { resultCode, orderNumber, statusCode, errorType } = response.data.paypalDetails;
      if (resultCode && orderNumber && statusCode === 200 && !errorType) {
        setIsLoading(false);
        if (resultCode !== 'Authorised') {
          setIsError(true);
        } else {
          dispatch(
            updatePaypalECS({
              successData: response.data.paypalDetails,
            })
          );
          dispatch(loadCart() as unknown as UnknownAction);
          dispatch(setStep(CartStep.SUCCESS));
          navigate(paths.CART, { replace: true });
        }
      } else {
        setIsLoading(false);
        setIsError(true);
      }
    }
  };

  useEffect(() => {
    if (!cart.items.length) {
      navigate(paths.CART, { replace: true });
    }
  }, [cart.items.length]);

  useEffect(() => {
    const currentTotal = parseFloat(total?.toString() ?? '0');
    const currentShipping = parseFloat(shippingAmount?.toString() ?? '0');
    const originalTotal = parseFloat(originalCart?.total?.toString() ?? '0');
    const originalShipping = parseFloat(originalCart?.shippingAmount?.toString() ?? '0');

    const cartItems = cart?.items ?? [];
    const originalItems = items ?? [];

    const currentNet = (currentTotal - currentShipping).toFixed(2);
    const originalNet = (originalTotal - originalShipping).toFixed(2);
    const totalsDiffer = currentNet !== originalNet;
    const itemsDiffer = JSON.stringify(cartItems) !== JSON.stringify(originalItems);

    if (totalsDiffer || itemsDiffer) {
      setIsError(true);
    }
  }, [originalCart?.total, total, items, cart?.items]);

  return !cart.items.length ? null : (
    <StyledReviewPage isError={isError}>
      <StyledHeader>
        <Logo isLoading={isLoading} />
      </StyledHeader>
      <h2 className="review-page-title">Détails de la commande</h2>
      <div className="review-page-content">
        <div className="review-page-product-container">
          <h2 className="review-page-title-mobile">Détails de la commande</h2>
          {items?.map((item) => {
            const id = `${item.productRef}${item.colorRef}${item.sku}`;
            return (
              <div key={id}>
                <CartItem
                  id={id}
                  item={item}
                  giftItemTextColor={giftItemTextColor}
                  quantity={item.quantity}
                  isOnlyView
                  isAvailable
                />
                <Divider />
              </div>
            );
          })}
        </div>
        <div className="review-page-informations-container">
          <div>
            <h3>Addresse de livraison</h3>
            <p>{deliveryStreet}</p>
            <p>{`${deliveryPostalCode} - ${deliveryCity}`}</p>
            <p>France</p>
          </div>
          <div>
            <h3>Addresse de facturation</h3>
            <p>{billingStreet}</p>
            <p>{`${billingPostalcode} - ${billingCity}`}</p>
            <p>{getRegionName(billingCountry)}</p>
          </div>
          <div className="review-page-phone-container">
            <h3>Téléphone</h3>
            <p>{phone}</p>
          </div>
          <div>
            <div className="review-page-total-container">
              <p className="light">Sous-total</p>
              <p className="light">{formatPrice(originalBaseTotal)}</p>
            </div>
            {totalCouponAmount > 0 && (
              <div className="review-page-total-container">
                <p className="light">Remise coupon</p>
                <p className="light discount-color">- {formatPrice(totalCouponAmount)}</p>
              </div>
            )}
            {totalDiscount > 0 && (
              <div className="review-page-total-container">
                <p className="light">Remise Totale</p>
                <p className="light discount-color">- {formatPrice(totalDiscount)}</p>
              </div>
            )}
            <div className="review-page-total-container">
              <p className="light">Frais de livraison</p>
              <p className="light">{formatPrice(shippingAmount)}</p>
            </div>
            <div className="review-page-total-container">
              <h3>TOTAL</h3>
              <p>{formatPrice(total)}</p>
            </div>
          </div>
          <Checkbox
            id={`paypal-express-review-page-optin-checkbox-${optinIsChecked}`}
            data-testid={`paypal-express-review-page-optin-checkbox-${optinIsChecked}`}
            onChange={() => setOptinIsChecked(!optinIsChecked)}
            checked={optinIsChecked}
          >
            <p className="paypal-express-review-page-news-letter-description">
              {PAYPAL_EXPRESS_OPT_IN}
            </p>
          </Checkbox>
          <button
            type="button"
            aria-label="Bouton pour finaliser mon paiement"
            className="papypal-express-pay-btn"
            data-testid="paypal-express-pay-btn"
            onClick={payEvent}
            disabled={isError || isLoading}
          >
            {isError ? (
              ERR_AUTH
            ) : isLoading ? (
              <Spinner color="black" />
            ) : (
              <>
                <img
                  height="20px"
                  src="https://www.paypalobjects.com/webstatic/en_US/i/buttons/PP_logo_h_100x26.png"
                  alt="PayPal"
                />
                <span className="paypal-express-btn-text">Valider mon paiement</span>
              </>
            )}
          </button>
          <button
            type="button"
            aria-label="Bouton pour annuler la commande"
            className="paypal-express-reviw-page-cancel-btn"
            data-testid="paypal-express-reviw-page-cancel-btn"
            onClick={() => navigate(paths.CART, { replace: true })}
            disabled={isLoading}
          >
            {PAYPAL_EXPRESS_CANCEL}
          </button>
        </div>
      </div>
    </StyledReviewPage>
  );
};
